/**
 * P3-1 线索列表 + 详情
 *
 * /marketing/leads → 列表（每 10 秒刷新）+ 筛选状态/星级
 * /marketing/leads/:id → 详情（AI 预分析卡片 + 时间线 + 分配按钮）
 */
import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Select, message, Empty } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import {
  PageHeader, HomeCard, Btn, Pill, TableWrap,
} from '../../components/ui';
// ux9 日期统一出口
import { fmtMonthDayTime } from '../../lib/format';

const STATUS_META: Record<string, { label: string; tone: 'success'|'warning'|'danger'|'default'|'primary' }> = {
  pending:            { label: '待分配', tone: 'warning' },
  assigned_by_ai:     { label: 'AI 自动分配', tone: 'primary' },
  assigned_by_manager:{ label: '主管已分配', tone: 'primary' },
  accepted:           { label: '已接单', tone: 'success' },
  fallback_to_pool:   { label: '超时回公海', tone: 'warning' },
  timeout:            { label: '超时', tone: 'danger' }, // 历史值(新逻辑改用 fallback_to_pool)，保留映射防旧数据显英文原值
};

function stars(n?: number | null) {
  if (!n) return '';
  return '⭐'.repeat(Number(n));
}

export function MarketingLeadsListPage() {
  const nav = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [minStars, setMinStars] = useState<number>(0);
  const params: any = { pageSize: 50 };
  if (statusFilter) params.status = statusFilter;
  if (minStars) params.minStars = minStars;

  const { data } = useQuery<any>({
    queryKey: ['marketing-leads', params],
    queryFn: () => $.get('/marketing/leads', params),
    refetchInterval: 10_000,
  });
  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        breadcrumb={['投流', '线索']}
        title={`线索（${data?.total ?? 0}）`}
        subtitle="实时刷新（每 10 秒）· 小红书私信留资 + AI 分析 + 分配状态"
      />
      <div className="work-content">
        <div style={{
          display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, padding: '8px 16px',
          background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8,
        }}>
          <span style={{ fontSize: 13, color: 'var(--text-3)' }}>状态:</span>
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            style={{ width: 160 }}
            options={[
              { value: '', label: '全部' },
              { value: 'pending', label: '待分配' },
              { value: 'assigned_by_ai', label: 'AI 自动' },
              { value: 'assigned_by_manager', label: '主管已分' },
              { value: 'accepted', label: '已接单' },
              { value: 'fallback_to_pool', label: '超时回公海' },
            ]}
          />
          <span style={{ fontSize: 13, color: 'var(--text-3)', marginLeft: 8 }}>最低意向:</span>
          <Select
            value={minStars}
            onChange={(v) => setMinStars(Number(v))}
            style={{ width: 120 }}
            options={[0, 1, 2, 3, 4, 5].map((n) => ({ value: n, label: n === 0 ? '全部' : `≥ ${stars(n)}` }))}
          />
        </div>

        <HomeCard title="实时线索">
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>时间</th>
                  <th>客户</th>
                  <th>地区</th>
                  <th>意向</th>
                  <th>留资</th>
                  <th>来源（计划/创意）</th>
                  <th>状态</th>
                  <th>加微</th>
                  <th>分配给</th>
                  <th>响应耗时</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
                    暂无线索 —— 配置完小红书 callback 后将自动显示
                  </td></tr>
                )}
                {items.map((l: any) => {
                  const st = STATUS_META[l.assignmentStatus] ?? STATUS_META.pending;
                  // v1.5 P3：加微状态展示
                  const wechatPill = l.wechatContactStatus === 'added'
                    ? <Pill tone="success">✅ 已加微</Pill>
                    : l.wechatContactStatus === 'failed'
                      ? <Pill tone="danger">❌ 失败</Pill>
                      : l.assignmentStatus === 'accepted'
                        ? <Pill tone="warning">⏳ 待加微</Pill>
                        : <span style={{ color: 'var(--text-3)' }}>—</span>;
                  return (
                    <tr key={l.id} style={{ cursor: 'pointer' }}
                        onClick={() => nav(`/marketing/leads/${l.id}`)}>
                      <td>{fmtMonthDayTime(l.receivedAt)}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{l.nickName ?? '—'}</div>
                        {l.aiProfileSummary && (
                          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{l.aiProfileSummary}</div>
                        )}
                      </td>
                      <td>{l.area ?? '—'}</td>
                      <td>{stars(l.aiIntentStars) || '—'}</td>
                      <td>
                        <Pill tone={l.infoStatus === '已留资' ? 'success' : 'default'}>{l.infoStatus ?? '—'}</Pill>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-3)' }}>
                        {l.campaignName ?? '—'}{l.creativityName ? ` / ${l.creativityName}` : ''}
                      </td>
                      <td><Pill tone={st.tone}>{st.label}</Pill></td>
                      <td>{wechatPill}</td>
                      <td>{l.assignedToUserId ? `#${l.assignedToUserId}` : '—'}</td>
                      <td>{l.salesResponseSeconds != null ? `${Math.round(l.salesResponseSeconds/60)} 分钟` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}

export function MarketingLeadDetailPage() {
  const { id } = useParams();
  const cid = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user)!;

  const { data } = useQuery<any>({
    queryKey: ['marketing-lead', cid],
    queryFn: () => $.get(`/marketing/leads/${cid}`),
  });
  const lead = data?.lead;
  const logs = data?.logs ?? [];

  const { data: usersList } = useQuery<any>({
    queryKey: ['sales-list-for-assign'],
    queryFn: () => $.get('/users?role=sales&pageSize=200'),
    enabled: ['admin','director','manager'].includes(user.role),
  });
  const salesOpts = (usersList?.items ?? usersList ?? [])
    .filter((u: any) => u.role === 'sales')
    .map((u: any) => ({ value: Number(u.id), label: `${u.realName} (#${u.id})` }));

  const [pickedUser, setPickedUser] = useState<number | null>(null);

  const autoAssign = useMutation({
    mutationFn: () => $.post(`/marketing/leads/${cid}/auto-assign`, {}),
    onSuccess: () => { message.success('已按 AI 推荐 Top 1 分配'); qc.invalidateQueries({ queryKey: ['marketing-lead', cid] }); },
  });
  const manualAssign = useMutation({
    mutationFn: () => $.post(`/marketing/leads/${cid}/assign`, { targetUserId: pickedUser }),
    onSuccess: () => { message.success('已分配'); qc.invalidateQueries({ queryKey: ['marketing-lead', cid] }); },
  });
  const accept = useMutation({
    mutationFn: () => $.post(`/marketing/leads/${cid}/accept`, {}),
    onSuccess: (r: any) => {
      message.success('已接单，进入客户');
      if (r.customerId) nav(`/customers/${r.customerId}`);
    },
  });
  // v1.5 P3：标记加微状态（销售接单后用）
  const markWechat = useMutation({
    mutationFn: (status: 'added' | 'failed') =>
      $.post(`/marketing/leads/${cid}/mark-wechat`, { status }),
    onSuccess: (_r: any, status) => {
      message.success(status === 'added' ? '✅ 已标记加微' : '已标记加微失败');
      qc.invalidateQueries({ queryKey: ['marketing-lead', cid] });
      qc.invalidateQueries({ queryKey: ['marketing-leads'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? '操作失败'),
  });

  if (!lead) return null;
  const canAssign = ['admin','director','manager'].includes(user.role) && lead.assignmentStatus === 'pending';
  const canAccept = user.role === 'sales' && Number(lead.assignedToUserId) === Number(user.id) && lead.assignmentStatus !== 'accepted';
  // 加微按钮：已接单 + (接单人 or manager+) + wechat 状态 != added
  const canMarkWechat = lead.assignmentStatus === 'accepted'
    && lead.wechatContactStatus !== 'added'
    && (Number(lead.assignedToUserId) === Number(user.id)
        || ['manager', 'director', 'admin'].includes(user.role));
  const rec = (lead.aiRecommendedUsersJson as any[]) ?? [];

  return (
    <>
      <PageHeader
        breadcrumb={['投流', '线索', lead.nickName ?? `#${lead.id}`]}
        title={`线索 · ${lead.nickName ?? '—'}`}
        tag={stars(lead.aiIntentStars)}
        subtitle={lead.aiProfileSummary ?? ''}
        actions={
          <>
            {canAssign && (
              <Btn kind="primary" onClick={() => autoAssign.mutate()} disabled={autoAssign.isPending}>
                {autoAssign.isPending ? '分配中…' : '按 AI Top 1 自动分配'}
              </Btn>
            )}
            {canAccept && (
              <Btn kind="primary" onClick={() => accept.mutate()} disabled={accept.isPending}>
                {accept.isPending ? '处理中…' : '✓ 立即跟进'}
              </Btn>
            )}
            {canMarkWechat && (
              <>
                <Btn kind="primary" onClick={() => markWechat.mutate('added')} disabled={markWechat.isPending}>
                  ✅ 标记已加微
                </Btn>
                <Btn kind="default" onClick={() => markWechat.mutate('failed')} disabled={markWechat.isPending}>
                  ❌ 加微失败
                </Btn>
              </>
            )}
            {lead.wechatContactStatus === 'added' && (
              <Pill tone="success">✅ 已加微 {lead.contactedAt ? `(${fmtMonthDayTime(lead.contactedAt)})` : ''}</Pill>
            )}
            {lead.wechatContactStatus === 'failed' && (
              <Pill tone="danger">❌ 加微失败</Pill>
            )}
            {lead.matchedCustomerId && (
              <Btn kind="default" onClick={() => nav(`/customers/${lead.matchedCustomerId}`)}>查看关联客户</Btn>
            )}
          </>
        }
      />
      <div className="work-content">
        {/* AI 预分析卡片（醒目） */}
        <div style={{
          padding: 16, marginBottom: 16,
          background: 'linear-gradient(135deg, rgba(124,77,255,0.06), rgba(99,200,180,0.06))',
          border: '1px solid var(--purple)', borderRadius: 8,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 18 }}>{stars(lead.aiIntentStars) || '⭐'}</span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{lead.aiProfileSummary ?? '画像未生成'}</span>
            {lead.aiRiskWarning && <Pill tone="danger">⚠️ {lead.aiRiskWarning}</Pill>}
          </div>
          {lead.aiFollowUpStrategy && (
            <div style={{ fontSize: 13, color: 'var(--text-1)', marginBottom: 12, lineHeight: 1.6 }}>
              <strong>策略：</strong>{lead.aiFollowUpStrategy}
            </div>
          )}
          {rec.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>AI 推荐 Top 3</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {rec.map((u: any, i: number) => (
                  <div key={i} style={{
                    padding: '8px 12px', background: 'var(--bg-1)', borderRadius: 6,
                    border: '1px solid var(--border)', minWidth: 180,
                  }}>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Top {i + 1}</div>
                    <div style={{ fontWeight: 500 }}>{u.name ?? `#${u.user_id}`}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{u.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canAssign && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>或手动选择销售员</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  value={pickedUser ?? undefined}
                  onChange={(v) => setPickedUser(Number(v))}
                  options={salesOpts}
                  style={{ flex: 1 }}
                  placeholder="选销售员"
                  showSearch
                  filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Btn kind="primary"
                     onClick={() => manualAssign.mutate()}
                     disabled={!pickedUser || manualAssign.isPending}>
                  {manualAssign.isPending ? '分配中…' : '分配'}
                </Btn>
              </div>
            </div>
          )}
        </div>

        <div className="home-grid">
          <HomeCard title="客户信息（来自小红书）">
            <div style={{ padding: 16, fontSize: 13 }}>
              <Kv label="昵称" v={lead.nickName} />
              <Kv label="小红书 ID" v={lead.redId} />
              <Kv label="地区" v={lead.area} />
              <Kv label="手机" v={lead.phoneNum} />
              <Kv label="微信" v={lead.wechat} />
              <Kv label="留资状态" v={lead.infoStatus} />
              <Kv label="复制微信" v={lead.wechatCopy ? '是' : '否'} />
              <Kv label="是否开口" v={lead.openTalk} />
              <Kv label="备注" v={lead.remark} />
            </div>
          </HomeCard>

          <HomeCard title="广告归因">
            <div style={{ padding: 16, fontSize: 13 }}>
              <Kv label="广告账户" v={lead.adAccount} />
              <Kv label="计划" v={lead.campaignName} />
              <Kv label="单元" v={lead.unitName} />
              <Kv label="创意" v={lead.creativityName} />
              <Kv label="私信接待" v={lead.msgReceiveName} />
              <Kv label="标签" v={lead.leadsTag} />
              {lead.noteLink && (
                <div style={{ marginTop: 8 }}>
                  <a href={lead.noteLink} target="_blank" rel="noopener noreferrer"
                     style={{ color: 'var(--primary)' }}>
                    🔗 笔记原文
                  </a>
                </div>
              )}
            </div>
          </HomeCard>
        </div>

        <HomeCard title={`分配时间线（${logs.length} 条）`}>
          <div style={{ padding: 16 }}>
            {logs.length === 0
              ? <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: 20 }}>暂无</div>
              : logs.map((l: any) => (
                <div key={l.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Pill tone={l.action === 'manager_picked' ? 'success' : 'primary'}>{l.action}</Pill>
                    <span style={{ fontSize: 12 }}>{l.reason ?? ''}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
                      {fmtMonthDayTime(l.createdAt)}
                    </span>
                  </div>
                  {l.targetUserId && (
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                      → 目标: #{l.targetUserId}
                    </div>
                  )}
                  {/* v1.2-rc3 Layer-1：manager_picked 展开 deficit / ratio 决策细节 */}
                  {l.action === 'manager_picked' && l.aiScoresJson && (
                    <div style={{
                      marginTop: 6, padding: 10, background: 'rgba(35,167,86,0.06)',
                      borderRadius: 4, fontSize: 12,
                    }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>策略</strong> ：{l.aiScoresJson.strategy}{' '}
                        <strong style={{ marginLeft: 12 }}>窗口</strong> ：{l.aiScoresJson.window}{' '}
                        <strong style={{ marginLeft: 12 }}>窗口起</strong> ：{l.aiScoresJson.windowStart}
                      </div>
                      <table style={{ width: '100%', fontSize: 12, marginTop: 4 }}>
                        <thead>
                          <tr style={{ color: 'var(--text-3)' }}>
                            <th style={{ textAlign: 'left' }}>主管</th>
                            <th>目标比例</th>
                            <th>已分</th>
                            <th>deficit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(l.aiScoresJson.ratios ?? []).map((r: any) => (
                            <tr key={r.manager_id} style={{
                              fontWeight: r.manager_id === l.aiScoresJson.picked ? 600 : 400,
                              color: r.manager_id === l.aiScoresJson.picked ? 'var(--success)' : undefined,
                            }}>
                              <td>#{r.manager_id}{r.manager_id === l.aiScoresJson.picked && ' ✓'}</td>
                              <td>{(r.target_ratio * 100).toFixed(1)}%</td>
                              <td>{r.current_count}</td>
                              <td>{r.deficit > 0 ? '+' : ''}{r.deficit}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </HomeCard>
      </div>
    </>
  );
}

function Kv({ label, v }: { label: string; v: any }) {
  if (!v && v !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
      <span style={{ color: 'var(--text-3)', minWidth: 70 }}>{label}：</span>
      <span style={{ flex: 1 }}>{String(v)}</span>
    </div>
  );
}
